import { useEffect } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "store";
import {
  setMessages,
  setNotifications,
  setSentRequests,
  setReceivedRequests,
  setPlayerWaitlist,
  setPlayerCurrentGame,
} from "store/actions";

import { db } from "config/firebase";
import {
  collection,
  onSnapshot,
  where,
  query,
  orderBy,
  limit,
  Unsubscribe,
} from "firebase/firestore";

interface UseUserListenersProps {
  userId: string | null;
}

/**
 * Custom hook that manages all user-specific Firebase listeners
 * Automatically handles cleanup when component unmounts or userId changes
 */
export const useUserListeners = ({ userId }: UseUserListenersProps) => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!userId) return;

    const unsubscribes: Unsubscribe[] = [];

    // Messages listener
    const messagesQuery = query(
      collection(db, "messages"),
      where("receiverId", "==", userId),
      orderBy("timeStamp", "desc"),
      limit(10),
    );

    const messagesUnsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messages = snapshot.docs.map((doc) => doc.data());
        console.log("Messages updated");
        dispatch(setMessages(messages));
      },
      (error) => {
        console.error("Messages listener error:", error);
      },
    );
    unsubscribes.push(messagesUnsubscribe);

    // Notifications listener
    const notificationsQuery = query(
      collection(db, "notifications"),
      where("receiverId", "==", userId),
      orderBy("timeStamp", "desc"),
    );

    const notificationsUnsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notifications = snapshot.docs.map((doc) => doc.data());
        console.log("Notifications updated");
        dispatch(setNotifications(notifications));
      },
      (error) => {
        console.error("Notifications listener error:", error);
      },
    );
    unsubscribes.push(notificationsUnsubscribe);

    // Received requests listener
    const receivedRequestsQuery = query(
      collection(db, "requests"),
      where("receiverId", "==", userId),
      orderBy("timeStamp", "desc"),
    );

    const receivedRequestsUnsubscribe = onSnapshot(
      receivedRequestsQuery,
      (snapshot) => {
        const requests: Record<string, any> = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          requests[data.id] = data;
        });
        dispatch(setReceivedRequests(requests));
      },
      (error) => {
        console.error("Received requests listener error:", error);
      },
    );
    unsubscribes.push(receivedRequestsUnsubscribe);

    // Sent requests listener
    const sentRequestsQuery = query(
      collection(db, "requests"),
      where("senderId", "==", userId),
      orderBy("timeStamp", "desc"),
    );

    const sentRequestsUnsubscribe = onSnapshot(
      sentRequestsQuery,
      (snapshot) => {
        const requests: Record<string, any> = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          requests[data.id] = data;
        });
        dispatch(setSentRequests(requests));
      },
      (error) => {
        console.error("Sent requests listener error:", error);
      },
    );
    unsubscribes.push(sentRequestsUnsubscribe);

    // Player waitlist listener
    const waitlistQuery = query(
      collection(db, "games"),
      where(`waitlist.${userId}`, "==", true),
    );

    const waitlistUnsubscribe = onSnapshot(
      waitlistQuery,
      (snapshot) => {
        const waitlist: Record<string, any> = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          waitlist[data.id] = data;
        });
        console.log("Player waitlist updated:", waitlist);
        dispatch(setPlayerWaitlist(waitlist));
      },
      (error) => {
        console.error("Player waitlist listener error:", error);
      },
    );
    unsubscribes.push(waitlistUnsubscribe);

    // Player current game listener
    const currentGameQuery = query(
      collection(db, "games"),
      where(`currentPlayers.${userId}`, "==", true),
    );

    const currentGameUnsubscribe = onSnapshot(
      currentGameQuery,
      (snapshot) => {
        const currentGame: Record<string, any> = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          currentGame[data.id] = data;
        });
        dispatch(setPlayerCurrentGame(currentGame));
      },
      (error) => {
        console.error("Player current game listener error:", error);
      },
    );
    unsubscribes.push(currentGameUnsubscribe);

    // Cleanup function
    return () => {
      console.log("Cleaning up user listeners");
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [userId, dispatch]);
};

export default useUserListeners;